/* eslint-disable sort-keys */
// keys are grouped in this file by meaning.

// External libraries
// @ts-expect-error
import * as beaver from '@krakenjs/beaver-logger';
// @ts-expect-error
import { isFunction } from '@checkout/react-components';

// Internal modules
import { X_APP_NAME } from '@/lib/constants';
import { PAGE_LOAD_PAYLOAD } from './fptiPayloads';
import { getParams as getUrlQueryParams } from '@/lib/utils/params';
import { getGlobals, getLocale, getToken } from '../globals';
import { logFPTI } from '@/lib/logger/fpti';
import { getReleaseMetaData } from '../globals';
import logPerformance from './cpl';
import { LOG_PREFIX } from '../constants';
import { setupCiLogger } from '@/lib/customerInteractions/setupCiLogger';
import { getIntegrationType } from './loggerUtils';

import type { GenericObjectType } from '@/types';

const {
  uri: { loggerEndpoint },
  device,
  initialGqlData,
} = getGlobals();

/*
 * Instantiate new instance of beaver-logger
 */
export const loggerInstance = beaver.Logger({
  url: loggerEndpoint, // URL to flush(send) the logs to
  initial_state_name: 'INIT_WEASLEY', // State name to post logs under
  autoLog: ['error', 'warn'], // Log levels which trigger an auto-flush to the server
  silent: process.env.NODE_ENV !== 'development', // Supress `console.log`s when `true`

  debounceInterval: 10, // Interval at which to debounce $beaver.flush calls
  flushInterval: 20000, // Interval at which to automatically flush logs to the server
  heartbeat: false, // Enable or disable heartbeats, which run on an interval
  heartbeatInterval: 5000, // Heartbeat log interval
  heartbeatMaxThreshold: 50, // Maximum number of sequential heartbeat logs
  heartbeatTooBusy: false, // Monitors for event loop delays and triggers a toobusy event
  heartbeatTooBusyThreshold: 10000, // Event loop delay which triggers a too busy event

  logPerformance: false, // Log performance stats from the browser automatically?
  logUnload: true, // Log window.onunload and window.beforeUnload events?
  logUnloadSync: true, // Log unload synchronously, to guarantee the log gets through?
  sizeLimit: 100, // Limit on number of logs before auto-flush happens
});

const { buttonSessionID, native_xo: nativeXo } = getUrlQueryParams();

// amplitude props
if (window?.xprops?.amplitude || nativeXo) {
  const user_id = window?.xprops?.buttonSessionID || buttonSessionID;

  loggerInstance.addMetaBuilder(() => ({ amplitude: true }));
  loggerInstance.addTrackingBuilder(() => ({ user_id: user_id }));
  loggerInstance.addPayloadBuilder(() => ({ user_id: user_id }));
}

// add  app-name header to each log event
loggerInstance.addHeaderBuilder(() => ({ 'x-app-name': X_APP_NAME }));

// add EC token to the payload for each log event
// NOTE: builder payload overrides user defined payloads in logger.* methods
loggerInstance.addPayloadBuilder(() => ({ token: getToken() })); // (for logger.__buffer__.events, NOT __buffer__.tracking (logger.track()) )

const { isBasl } = getGlobals();

/**
 * @function flushThen
 * @param {func} fn
 * @description Flushes logs and then executes a callback. This
 * is primarily used when redirecting to another page. Before redirecting
 * we flush the logs then we execute the redirect function.
 */
const flushThen = (fn: () => void): unknown => {
  if (!isFunction(fn)) {
    throw new TypeError(
      `expected argument to be a function but got ${typeof fn}`,
    );
  }

  return loggerInstance.immediateFlush().then(fn).catch(fn);
};

const createCustomLogger = (logMethod: string) => {
  return (logName: string, payload: GenericObjectType = {}) => {
    const { locale, country } = getLocale();
    const key = LOG_PREFIX.test(logName) ? logName : `weasley_${logName}`;

    loggerInstance[logMethod](key, {
      // Common payload
      clientCountry: country,
      clientLocale: locale,
      clientTimestamp: Date.now(),
      ...payload,
    });
  };
};

type MetricDataPayloadType = {
  dimensions: Record<string, string | boolean | number>;
};

/**
 * @function metricLogger
 * @param {string} metricEventName
 * @param {object} payload: {dimensions:{}}
 * @description Adds data to metric[] array in /logger beaver-logger ajax array.
 * Then sends data to loggernodeweb and eventually signalFfx.
 */
const logMetric = (
  metricEventName: string, // ends up under the `event` dimension
  payload: MetricDataPayloadType = {} as MetricDataPayloadType,
): undefined => {
  const { deviceType } = device;
  const { date } = getReleaseMetaData();
  const region = initialGqlData?.localeMetadata?.businessRegions?.[0] ?? ''; // different from geoRegion (see globals)

  if (!loggerInstance.metric) {
    return; // noop if we don't have a version of beaver-logger loaded that supports `.metric()`
  }

  // in DEV/TEST mode only we throw when more than 4 dimensions are passed
  // (helps limit cardinality problems with sfx.)
  if (
    process.env.NODE_ENV === 'development' ||
    process.env.NODE_ENV === 'test'
  ) {
    if (Object.keys(payload?.dimensions ?? {}).length > 4) {
      throw new Error('logger.metric() has too many dimensions (>4). ');
    }
  }

  loggerInstance.metric(
    Object.assign(
      { metricEventName },
      {
        // first param is ignored on loggernodeweb (beaver-logger-paypal)
        // TODO eventually we can allow overriding these values in payload param
        clientTimestamp: Date.now(),
        metricEventName: metricEventName, // this will be assigned to the `event` dimension in signalFx
        metricNamespace: 'pp.xo.ui.custom', // beaver-logger-paypal handles converting this to a signalFx metricName
        metricValue: 1,
      },
      payload,
      {
        // ! We want to be very careful adding more dimensions here and want to
        // limit the amount of values as this can cause cardinality problems
        // for sfx and bring down sfx.
        dimensions: {
          clientApp: 'weasley',
          region, // using region as country / locale had too much variance and would cause cardinality issues
          deviceType, // DESKTOP|TABLET|MOBILE
          integrationType: getIntegrationType(),
          // booleans
          isBasl,
          pageRefresh:
            window?.performance?.navigation?.type ===
            window?.performance?.navigation?.TYPE_RELOAD,
          releaseDate: date, // 03/17
          ...payload.dimensions, // TODO: limit to 4 user added dimensions (maybe fewer?)
        },
      },
    ),
  );
};

type configureOpts = {
  loggerEndpoint: string;
  token: string;
};

export interface LoggerType {
  /**
   * It adds metadata to (events/tracking/metrics) when the logs are flushed
   */
  addMetadata: (metadata: GenericObjectType) => void;
  cpl: {
    logPerformance(customParams: GenericObjectType): void;
  };

  error: ReturnType<typeof createCustomLogger>;
  info: ReturnType<typeof createCustomLogger>;
  warn: ReturnType<typeof createCustomLogger>;

  flush(): Promise<void>;
  flushThen: typeof flushThen;

  metric: typeof logMetric;
  atomicMetric: (payload?: any) => void;
  track: typeof loggerInstance.track;
  fpti: typeof logFPTI;
  fptiTrackPageLoad: FPTITrackPageLoadType;

  // mainly provided for testing
  configure(opts: configureOpts): void; // returns nothing

  __buffer__: {
    events: GenericObjectType[];
    tracking: GenericObjectType[];
    metrics: GenericObjectType[];
  };
}

export const logger: LoggerType = {
  addMetadata: (metadata): void => {
    loggerInstance.addMetaBuilder(() => metadata);
  },
  cpl: {
    logPerformance: function (...args) {
      logPerformance.apply(logger, args);
    },
  },

  error: createCustomLogger('error'),
  info: createCustomLogger('info'),
  warn: createCustomLogger('warn'),

  // FIXME: Can we combine these? Simplify the implementation?
  flush: loggerInstance.immediateFlush,
  flushThen,

  atomicMetric: payload => {
    // if we have a version of logger which does not support metric then do nothing
    if (!loggerInstance?.metric) {
      return;
    }
    loggerInstance.metric(payload);
  },
  metric: logMetric,
  track: loggerInstance.track,
  fpti: function (...args) {
    logFPTI.apply(this, args);
  },
  fptiTrackPageLoad: {} as FPTITrackPageLoadType, // replaced down below

  // mainly provided for testing
  configure: (opts: configureOpts) => {
    const { loggerEndpoint, token } = opts;

    loggerInstance.configure({ url: loggerEndpoint }); // configure logging endpoint
    loggerInstance.addPayloadBuilder(() => {
      // update token for all the payloads
      return {
        token: token,
      };
    });
  },

  // exposed for testing only
  __buffer__: loggerInstance.__buffer__,
};

// Setup Client Interactions logger
setupCiLogger(loggerInstance, logger);

interface FPTITrackPageLoadType {
  handleItemLoaded: (name: string) => void;
  items: Set<string>;
  registerItem: (name: string) => void;
}

/**
 * Register lazy loaded inputs so that we can track when they load and
 * fire off an FPTI log for the page load event once everything is loaded.
 * Allows us to track the loading status for certain items.
 */
logger.fptiTrackPageLoad = {
  handleItemLoaded: function (name) {
    this.items.delete(name);

    // once the last item was deleted, submit the page_loaded event
    if (!this.items.size) {
      const payload = PAGE_LOAD_PAYLOAD(true);

      logFPTI.call(logger, 'weasley_page_loaded', payload);
    }
  },
  items: new Set(),
  registerItem: function (name) {
    this.items.add(name);
  },
};
